"use client"

// DataTable.js
import { useState, useEffect, useCallback, useContext } from "react"
import { PlanContext } from "../App"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faExternalLinkAlt,
  faDownload,
  faEye,
  faCaretDown,
  faChevronLeft,
  faFilter,
  faCheckSquare,
  faSquare,
} from "@fortawesome/free-solid-svg-icons"
import { downloadSingleProductCSV, downloadCollectionProductsCSV } from "../components/downloadSingleProductCSV"

const DataTable = ({
  data,
  exportType,
  onViewCollectionProducts,
  isViewingCollectionProducts,
  onBackToCollections,
  websiteUrl,
  exportPlatform,
  currentPage, // New prop
  productsPerPage, // New prop
}) => {
  const [selectedRows, setSelectedRows] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [vendorFilter, setVendorFilter] = useState("------")
  const [displayData, setDisplayData] = useState([])
  const [uniqueVendors, setUniqueVendors] = useState(["------"])

  const plan = useContext(PlanContext)

  const getFilteredData = useCallback(() => {
    let filtered = data || []

    if (exportType === "Products" && vendorFilter !== "------") {
      filtered = filtered.filter((item) => item.vendor === vendorFilter)
    }

    return filtered
  }, [data, vendorFilter, exportType])

  useEffect(() => {
    setDisplayData(data || [])
  }, [data])

  useEffect(() => {
    setDisplayData(getFilteredData())
  }, [data, vendorFilter, getFilteredData])

  useEffect(() => {
    if (exportType === "Products" && data) {
      const vendors = [...new Set(data.map((item) => item.vendor))].sort()
      setUniqueVendors(["------", ...vendors])
    } else {
      setUniqueVendors(["------"])
    }
  }, [data, exportType])

  const handleSelectAll = useCallback(() => {
    const dataToUse = getFilteredData()
    setSelectedRows(selectAll ? [] : dataToUse.map((item) => item.id))
    setSelectAll(!selectAll)
  }, [selectAll, getFilteredData])

  const handleRowSelect = useCallback(
    (id) => {
      setSelectedRows(selectedRows.includes(id) ? selectedRows.filter((row) => row !== id) : [...selectedRows, id])
    },
    [selectedRows],
  )

  const handleVendorFilter = useCallback((e) => {
    setVendorFilter(e.target.value)
  }, [])

  const handleSingleProductDownload = useCallback(
    (productHandle, productIndex) => {
      // Calculate the actual product position considering pagination
      const actualPosition =
        productsPerPage && currentPage ? (currentPage - 1) * productsPerPage + productIndex + 1 : productIndex + 1

      // Check if user is on free plan and product is beyond 50th position
      if ((plan === "Free" || plan === "trial") && actualPosition > 50) {
        alert(
          "You can only download CSV for the first 50 products with a free account. Please upgrade your plan to download more products.",
        )
        return
      }

      if (websiteUrl && exportPlatform && productHandle) {
        downloadSingleProductCSV(websiteUrl, productHandle, exportPlatform)
      } else {
        console.error("Base URL, export platform, or product handle is missing for single product download.")
        alert("Could not download product data.")
      }
    },
    [websiteUrl, exportPlatform, plan, productsPerPage, currentPage],
  )

  const handleCollectionProductsDownload = useCallback(
    (collectionHandle) => {
      // Restrict collection CSV download for free users
      if (plan === "Free" || plan === "trial") {
        alert(
          "Collection CSV download is not available for free accounts. Please upgrade your plan to access this feature.",
        )
        return
      }

      if (websiteUrl && exportPlatform && collectionHandle) {
        downloadCollectionProductsCSV(websiteUrl, collectionHandle, exportPlatform)
      } else {
        console.error("Base URL, export platform, or collection handle is missing for collection products download.")
        alert("Could not download collection products data.")
      }
    },
    [websiteUrl, exportPlatform, plan],
  )

  return (
    <div className="data-table-container">
      <style jsx="true">{`
        .data-table-container {
          width: 100%;
          margin: 1.5rem 0;
          overflow-x: auto;
        }

        .back-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background-color: #007BFF;
          color: white;
          border: none;
          padding: 0.75rem 1.25rem;
          border-radius: 0.375rem;
          font-weight: 600;
          cursor: pointer;
          margin-bottom: 1rem;
          transition: background-color 0.2s ease;
        }

        .back-button:hover {
          background-color: #008f85;
        }

        .data-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          overflow: hidden;
        }

        .table-header {
          background-color: #f33a3a;
          color: white;
          text-align: left;
          padding: 1rem;
          font-weight: 600;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .table-header:first-child {
          border-top-left-radius: 0.5rem;
        }

        .table-header:last-child {
          border-top-right-radius: 0.5rem;
        }

        .table-cell {
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
          text-align: left;
          vertical-align: top;
        }

        .table-row:hover {
          background-color: #f9fafb;
        }

        .table-row:last-child .table-cell {
          border-bottom: none;
        }

        .checkbox-container {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .checkbox-icon {
          color: #007BFF;
          cursor: pointer;
          font-size: 1.25rem;
        }

        .row-number {
          color: #6b7280;
          font-size: 0.875rem;
        }

        .product-image {
          width: 110px;
          height: 100px;
          object-fit: cover;
          border-radius: 0.375rem;
          border: 1px solid #e5e7eb;
          transition: transform 0.2s ease;
        }

        .product-image:hover {
          transform: scale(1.05);
        }

        .product-title {
          font-weight: 500;
          color: #111827;
        }

        .product-vendor {
          color: #4b5563;
        }

        .product-description {
          color: #6b7280;
          font-size: 0.875rem;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .product-total {
          font-weight: 600;
          color: #007BFF;
        }

        .action-container {
          display: flex;
          justify-content: center;
          gap: 0.75rem;
        }

        .action-icon {
          cursor: pointer;
          transition: transform 0.2s ease, color 0.2s ease;
          font-size: 1.125rem;
        }

        .action-icon-link {
          color: #6b7280;
        }

        .action-icon-link:hover {
          color: #111827;
          transform: translateY(-2px);
        }

        .action-icon-download {
          color: #f33a3a;
        }

        .action-icon-download:hover {
          color: #e02e2e;
          transform: translateY(-2px);
        }

        .action-icon-view {
          color: #007BFF;
        }

        .action-icon-view:hover {
          color: #008f85;
          transform: translateY(-2px);
        }

        .vendor-filter-container {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          position: relative;
          width: 100%;
        }

        .vendor-label {
          white-space: nowrap;
        }

        .vendor-select {
          appearance: none;
          background-color: transparent;
          border: none;
          color: white;
          padding-right: 1.5rem;
          cursor: pointer;
          font-size: inherit;
          width: 100%;
          outline: none;
        }

        .vendor-select option {
          color: #111827;
          background-color: white;
        }

        .caret-icon {
          position: absolute;
          right: 0;
          pointer-events: none;
        }

        .empty-message {
          text-align: center;
          padding: 2rem;
          color: #6b7280;
          font-style: italic;
        }

        .action-icon-disabled {
          color: #d1d5db;
          cursor: not-allowed;
        }

        .action-icon-disabled:hover {
          color: #d1d5db;
          transform: none;
        }

        @media (max-width: 768px) {
          .data-table {
            font-size: 0.875rem;
          }
          .table-header, .table-cell {
            padding: 0.75rem 0.5rem;
          }
          .product-image {
            width: 80px;
            height: 80px;
          }
          .action-container {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      `}</style>
      {isViewingCollectionProducts && (
        <button className="back-button" onClick={onBackToCollections}>
          <FontAwesomeIcon icon={faChevronLeft} /> Back to Collections
        </button>
      )}
      <table className="data-table">
        <thead>
          <tr>
            <th className="table-header" style={{ width: "80px" }}>
              <div className="checkbox-container">
                <FontAwesomeIcon
                  icon={selectAll ? faCheckSquare : faSquare}
                  className="checkbox-icon"
                  onClick={handleSelectAll}
                />
                <span>#</span>
              </div>
            </th>
            {exportType === "Products" && (
              <>
                <th className="table-header">Image</th>
                <th className="table-header">Title</th>
                <th className="table-header">
                  <div className="vendor-filter-container">
                    <label htmlFor="vendor-filter" className="vendor-label">
                      <FontAwesomeIcon icon={faFilter} /> Vendor
                    </label>
                    <select
                      id="vendor-filter"
                      className="vendor-select"
                      value={vendorFilter}
                      onChange={handleVendorFilter}
                    >
                      {uniqueVendors.map((vendor) => (
                        <option key={vendor} value={vendor}>
                          {vendor}
                        </option>
                      ))}
                    </select>
                    <FontAwesomeIcon icon={faCaretDown} className="caret-icon" />
                  </div>
                </th>
              </>
            )}
            {exportType === "Collections" && (
              <>
                <th className="table-header">Title</th>
                <th className="table-header">Description</th>
              </>
            )}
            {exportType === "CollectionProducts" && (
              <>
                <th className="table-header">Image</th>
                <th className="table-header">Title</th>
                <th className="table-header">Vendor</th>
              </>
            )}
            <th className="table-header">{exportType === "Collections" ? "Total Products" : "Actions"}</th>
          </tr>
        </thead>
        <tbody>
          {displayData.length > 0 ? (
            displayData.map((item, index) => (
              <tr key={item.id} className="table-row">
                <td className="table-cell">
                  <div className="checkbox-container">
                    <FontAwesomeIcon
                      icon={selectedRows.includes(item.id) ? faCheckSquare : faSquare}
                      className="checkbox-icon"
                      onClick={() => handleRowSelect(item.id)}
                    />
                    <span className="row-number">
                      {productsPerPage && currentPage ? (currentPage - 1) * productsPerPage + index + 1 : index + 1}
                    </span>
                  </div>
                </td>
                {exportType === "Products" && (
                  <>
                    <td className="table-cell">
                      {item.image && (
                        <img src={item.image || "/placeholder.svg"} alt={item.title} className="product-image" />
                      )}
                    </td>
                    <td className="table-cell">
                      <div className="product-title">{item.title}</div>
                      <div className="product-vendor">{item.vendor}</div>
                    </td>
                    <td className="table-cell">{item.vendor}</td>
                  </>
                )}
                {exportType === "Collections" && (
                  <>
                    <td className="table-cell">
                      <div className="product-title">{item.title}</div>
                    </td>
                    <td className="table-cell">
                      <div className="product-description">{item.description}</div>
                    </td>
                  </>
                )}
                {exportType === "CollectionProducts" && (
                  <>
                    <td className="table-cell">
                      {item.image && (
                        <img src={item.image || "/placeholder.svg"} alt={item.title} className="product-image" />
                      )}
                    </td>
                    <td className="table-cell">
                      <div className="product-title">{item.title}</div>
                    </td>
                    <td className="table-cell">
                      <div className="product-vendor">{item.vendor}</div>
                    </td>
                  </>
                )}
                <td className="table-cell">
                  <div className="action-container">
                    {exportType === "Collections" ? (
                      <div className="product-total">{item.total}</div>
                    ) : (
                      <>
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-icon action-icon-link"
                          title="View on website"
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                        </a>
                        <FontAwesomeIcon
                          icon={faDownload}
                          className={`action-icon ${
                            (plan === "Free" || plan === "trial") &&
                            (
                              exportType === "CollectionProducts" ||
                                (productsPerPage && currentPage
                                  ? (currentPage - 1) * productsPerPage + index + 1
                                  : index + 1) > 50
                            )
                              ? "action-icon-disabled"
                              : "action-icon-download"
                          }`}
                          title={
                            (plan === "Free" || plan === "trial") &&
                            (exportType === "CollectionProducts" ||
                              (productsPerPage && currentPage
                                ? (currentPage - 1) * productsPerPage + index + 1
                                : index + 1) > 50)
                              ? "Upgrade plan to download products beyond the first 50"
                              : "Download product"
                          }
                          onClick={() => {
                            const actualPosition =
                              productsPerPage && currentPage
                                ? (currentPage - 1) * productsPerPage + index + 1
                                : index + 1

                            if (
                              (plan === "Free" || plan === "trial") &&
                              (exportType === "CollectionProducts" || actualPosition > 50)
                            ) {
                              alert(
                                "This feature is not available for free accounts or you've reached the 50 product limit. Please upgrade your plan.",
                              )
                              return
                            }
                            handleSingleProductDownload(item.handle, index)
                          }}
                          style={{
                            cursor:
                              (plan === "Free" || plan === "trial") &&
                              (exportType === "CollectionProducts" ||
                                (productsPerPage && currentPage
                                  ? (currentPage - 1) * productsPerPage + index + 1
                                  : index + 1) > 50)
                                ? "not-allowed"
                                : "pointer",
                          }}
                        />
                      </>
                    )}
                    {exportType === "Collections" && (
                      <>
                        <FontAwesomeIcon
                          icon={faDownload}
                          className={`action-icon ${
                            plan === "Free" || plan === "trial" ? "action-icon-disabled" : "action-icon-download"
                          }`}
                          title={
                            plan === "Free" || plan === "trial"
                              ? "Upgrade plan to download collection products"
                              : "Download collection products"
                          }
                          onClick={() => {
                            if (plan === "Free" || plan === "trial") {
                              alert(
                                "Collection CSV download is not available for free accounts. Please upgrade your plan.",
                              )
                              return
                            }
                            handleCollectionProductsDownload(item.handle)
                          }}
                          style={{
                            cursor: plan === "Free" || plan === "trial" ? "not-allowed" : "pointer",
                          }}
                        />

                        <FontAwesomeIcon
                          icon={faEye}
                          className="action-icon action-icon-view"
                          title="View collection products"
                          onClick={() => {
                            if (onViewCollectionProducts && item.handle) {
                              onViewCollectionProducts(item.handle)
                            } else {
                              console.error("onViewCollectionProducts prop or collection handle is missing.")
                              alert("Could not view collection products.")
                            }
                          }}
                        />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={exportType === "Products" || exportType === "CollectionProducts" ? 5 : 5}
                className="table-cell empty-message"
              >
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export default DataTable
